import type React from "react";
import { ApiClient, ApiResp } from "./ApiClient";

// ============ AI 智能分析 周报 ============
// 后端基座：AiCoachController，挂载在 /api/mis/ai-coach 下。
// ApiClient 的 endpoint 已包含 /api/mis 前缀，因此这里只用相对路径。

/** 意向等级 → 中文（与后端 lead_scoring system_prompt 意向程度定义同源） */
export const INTENTION_LEVEL_LABEL: Record<string, string> = {
  A: "高意向",
  B: "中意向",
  C: "低意向",
  D: "无意向",
  E: "待定",
};

export function intentionLevelLabel(lvl?: string): string {
  if (!lvl) return "-";
  return INTENTION_LEVEL_LABEL[lvl] ?? lvl;
}

/** 意向等级圆形徽章样式（A绿/B黄/C橙/D红/E灰） */
export function intentionLevelStyle(lvl?: string): React.CSSProperties {
  if (!lvl) return {};
  const bg =
    lvl === "A" ? "#22c55e" :
    lvl === "B" ? "#facc15" :
    lvl === "C" ? "#f97316" :
    lvl === "D" ? "#ef4444" :
    "#9ca3af";
  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 22,
    height: 22,
    borderRadius: "50%",
    background: bg,
    color: "#fff",
    fontSize: 12,
    fontWeight: 700,
  };
}

/** 生成报告请求 */
export interface AiCoachGenerateReq {
  periodStart?: string; // yyyy-MM-dd，可选，默认本周一
  periodEnd?: string;   // yyyy-MM-dd，可选，默认本周日
  /** 目标销售 ID。仅 区长/运营主管/管理员 可传，销售角色传了也会被后端忽略。 */
  salesId?: number;
  /** 强制重新生成。前端"重新生成"按钮（report 已存在时）传 true，
   *  后端跳过 READY/PROCESSING 复用，直接覆盖重跑。 */
  force?: boolean;
}

export interface AiCoachMetrics {
  totalCustomers: number;
  newCustomers: number;
  analyzedCustomers: number;
  abTotal: number;
  abWithResistanceTotal?: number;
  highStarRatio: number;
  effectiveIntentionRate: number;
  abVisitRate: number;
  resistanceRatio: number;
  lowQualityIdleRatio: number;
  timeAllocationRatio?: number;
  resistanceResolutionRate?: number;
  highStarIdleCount?: number;
  highScoreIdleCount?: number;
}

/** 单条调整建议 */
export interface AiCoachAdjustment {
  title: string;
  action: string;
  weeklyGoal: string;
}

/** Top 抗拒点 */
export interface AiCoachResistance {
  term: string;
  rawExamples: string[];
  frequency: number;
  category: string;
  script?: string;
}

/** 客户针对性诊断（每条对应一个 leadId） */
export interface AiCoachCustomerDiagnosis {
  leadId: number;
  /** 销售沟通技巧问题（基于录音或 lastCallNote 分析） */
  techniqueIssues: string[];
  /** 促成交易技巧 */
  closingTechniques: string[];
  /** 可照念的经典话术 */
  classicScripts: string[];
  /** 下一步具体动作 */
  nextAction: string;
  /** v3：后端合并的客户元信息快照（取自报告生成时的 leads 表） */
  leadSnapshot?: {
    leadName?: string;
    star?: number;
    status?: string;
    intentionLevel?: string;
    score?: number;
  };
}

/** LLM 诊断结论 */
export interface AiCoachDiagnosis {
  coreProblem: string;
  adjustments: AiCoachAdjustment[];
  topResistances: AiCoachResistance[];
  scriptAmmo: string;
  /** v3：每客户针对性诊断，leadId 与 TOP 客户摘要一一对应 */
  customerDiagnoses?: AiCoachCustomerDiagnosis[];
}

/** 周报主体 */
export interface AiReport {
  id: number;
  salesId: number;
  periodKey: string;
  periodStart: string;
  periodEnd: string;
  metricsJson: AiCoachMetrics | null;
  triggeredTags: string[] | null;
  llmDiagnosis: AiCoachDiagnosis | null;
  status: "processing" | "ready" | "failed";
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
}

/** 行动追踪记录 */
export interface AiActionTrack {
  id: number;
  reportId: number;
  salesId: number;
  adjustmentIndex: number;
  plannedAction: string | null;
  status: "pending" | "in_progress" | "done" | "rejected";
  completionNote: string | null;
  createdAt: string;
  updatedAt: string;
}

/** 创建行动请求 */
export interface AiCoachCreateActionReq {
  adjustmentIndex: number;
  plannedAction: string;
}

/** 更新行动请求 */
export interface AiCoachUpdateActionReq {
  status: string;
  completionNote?: string;
}

/** 生成报告响应 */
export interface AiCoachGenerateResp {
  reportId: number;
  status: string;
}

/** 通话文字稿（用于"看原文"弹窗） */
export interface AiCallTranscript {
  callLogId: number;
  leadName: string;
  callAt: string;
  durationSec: number;
  transcriptText: string;
}

/** 指标 / 抗拒点明细 —— 单条客户 */
export interface BreakdownItem {
  id: number;
  name: string;
  leadNo: string;
  intentionLevel?: string;
  star?: number;
  status?: string;
  /** 资质（leads.remark 原文） */
  remark?: string;
  /** 跟进记录（lead_contact_text.contact_logs，JSON 数组） */
  contactLogs?: string[];
  lastContactAt?: string;
  assignedAt?: string;
  weekCallCount: number;
  resistancePoints: string[];
}

const api = ApiClient.getDefault();

/** LLM 原始输出用 snake_case，前端接口用 camelCase。这里做一层归一化。 */
function snakeToCamel(s: string): string {
  return s.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
}

function deepNormalize<T>(v: T): T {
  if (Array.isArray(v)) return (v as unknown[]).map(deepNormalize) as unknown as T;
  if (v && typeof v === "object") {
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(v as Record<string, unknown>)) {
      out[snakeToCamel(k)] = deepNormalize((v as Record<string, unknown>)[k]);
    }
    return out as unknown as T;
  }
  return v;
}

function normalizeReport<T extends AiReport | null>(r: T): T {
  if (!r || !r.llmDiagnosis) return r;
  return { ...r, llmDiagnosis: deepNormalize(r.llmDiagnosis) } as T;
}

function normalizeReportList(resp: ApiResp<AiReport[] | null>) {
  if (!resp.data) return resp;
  return { ...resp, data: resp.data.map(normalizeReport) };
}

export const AiCoachApi = {
  /** 生成（或重新生成）本周报告。管理员可带 salesId 指定目标销售 */
  generateReport: (req?: AiCoachGenerateReq) =>
    api.request<AiCoachGenerateResp>(
      "/ai-coach/reports/generate",
      "POST",
      undefined,
      req
    ),
  /** 获取本周报告（无则返回 null）。管理员可带 salesId */
  getCurrentReport: (date?: string, salesId?: number) =>
    api
      .request<AiReport | null>(
        "/ai-coach/reports/current",
        "GET",
        { ...(date ? { date } : {}), ...(salesId ? { salesId } : {}) }
      )
      .then((resp) => ({ ...resp, data: normalizeReport(resp.data) })),
  /** 按 ID 取单条报告（用于轮询） */
  getReport: (reportId: number) =>
    api
      .request<AiReport>(`/ai-coach/reports/${reportId}`, "GET")
      .then((resp) => ({ ...resp, data: normalizeReport(resp.data) })),
  /** 历史报告分页。管理员可带 salesId */
  listReports: (page = 1, size = 20, salesId?: number) =>
    api
      .request<AiReport[]>("/ai-coach/reports", "GET", { page, size, ...(salesId ? { salesId } : {}) })
      .then(normalizeReportList),
  /** 把一条调整建议落为行动 */
  createAction: (reportId: number, req: AiCoachCreateActionReq) =>
    api.request<AiActionTrack>(
      `/ai-coach/reports/${reportId}/actions`,
      "POST",
      undefined,
      req
    ),
  /** 更新行动状态 / 完成备注 */
  updateAction: (actionId: number, req: AiCoachUpdateActionReq) =>
    api.request<AiActionTrack>(
      `/ai-coach/actions/${actionId}`,
      "PUT",
      undefined,
      req
    ),
  /** 名下全部行动（用于跨周未完成 banner）。管理员可带 salesId */
  listActions: (salesId?: number) =>
    api.request<AiActionTrack[]>("/ai-coach/actions", "GET", salesId ? { salesId } : undefined),
  /** 某条报告下的全部行动（含 done/rejected）。给"本周已接受行动"面板用 */
  listActionsByReport: (reportId: number) =>
    api.request<AiActionTrack[]>(`/ai-coach/reports/${reportId}/actions`, "GET"),
  /** 指标对应的客户明细 */
  metricBreakdown: (reportId: number, key: string) =>
    api.request<BreakdownItem[]>(
      `/ai-coach/reports/${reportId}/metric-breakdown`,
      "GET",
      { key }
    ),
  /** 抗拒点 term 对应的客户明细 */
  resistanceBreakdown: (reportId: number, term: string) =>
    api.request<BreakdownItem[]>(
      `/ai-coach/reports/${reportId}/resistance-breakdown`,
      "GET",
      { term }
    ),
  /** 取一条通话记录的文字稿（脱敏后） */
  getCallLogTranscript: (callLogId: number) =>
    api.request<AiCallTranscript>(
      `/ai-coach/call-logs/${callLogId}/transcript`,
      "GET"
    ),
  /** 转写约束（开关 + 时长窗口），前端预校验"转文字"按钮可用性 */
  getTranscriptLimits: () =>
    api.request<{ enabled: boolean; minDurationSec: number; maxDurationSec: number }>(
      "/ai-coach/call-logs/transcript-limits",
      "GET"
    ),
  /** 提交单条通话记录进行转写 */
  submitTranscript: (callLogId: number) =>
    api.request<string>(
      `/ai-coach/call-logs/${callLogId}/transcript`,
      "POST"
    ),
  /** 手动触发文字稿扫描（运维/调试用） */
  scanTranscripts: () =>
    api.request<boolean>("/ai-coach/call-logs/scan", "POST"),
};
